import React, { useEffect, useState } from 'react';
import { components as coreComponents } from '@tempi/core-elements/dist/renderer';
import {
  buildComponentDict,
  BuilderComponent,
  Device,
  BuilderConfig,
} from '@tempi/core-renderer';
import { components as ecomComponents } from '@tempi/ecom-elements/dist/renderer';

import { PbServerSideProps } from './getServerSideProps';
import { useTenantConfig, useTerraScreenConfig } from 'hooks';
import { mappingDynamicDesktopSlots, mappingDynamicMobileSlots } from './slots';
import { usePbPage } from './hooks';
import { getEnv } from 'helpers';
import {
  DynamicSlotComponents,
  ElementInterface,
  ExtraStoreState,
} from './types';
import { ScreenConfig } from '@terra-js/shop-front-kit';
import dynamic from 'next/dynamic';
import { useAuth } from '@teko/auth-react';
import terraKit from '@terra-js/terra-kit';

export type PbPageProps = PbServerSideProps & {
  page?: string;
};
const BackButtonHeaderMobile = dynamic(() =>
  import('./slots/BackButtonHeaderMobile').then(
    (f) => f.BackButtonHeaderMobile,
  ),
);

export const PbPage: React.FC<PbPageProps> = ({
  pbContent: serverContent,
  pbSSRData,
  menu: serverMenu,
  page = '/',
}: PbPageProps) => {
  const { isMobile, terminal, location, terminalId } = useTenantConfig();
  const device: Device = isMobile ? 'mobile' : 'desktop';
  const { content, menu, websiteId } = usePbPage({
    page: page,
    device,
    defaultValue: {
      content: serverContent,
      menu: serverMenu || [],
    },
  });
  const { partner, getProcessEnv, isDisableLuckyDrawPhoneInput } =
    useTenantConfig();
  const screenConfig: ScreenConfig | undefined = useTerraScreenConfig();
  const backappUrl = getProcessEnv('BACKAPP_URL');
  const { user } = useAuth();
  const [appUserPhone, setAppUserPhone] = useState<string>();
  const [appUserLoading, setAppUserLoading] = useState<boolean>(true);

  const showBackButton =
    !screenConfig?.header?.isBackIconHidden && (!!partner || !!backappUrl);

  const mappingDynamicMobileSlotsAddNew: DynamicSlotComponents = {
    ...mappingDynamicMobileSlots,
    ...(showBackButton
      ? {
          back_button_header_mobile: BackButtonHeaderMobile,
        }
      : {}),
  };

  const mappingDynamicSlots =
    device === 'mobile'
      ? mappingDynamicMobileSlotsAddNew
      : mappingDynamicDesktopSlots;

  useEffect(() => {
    if (terraKit.isAvailable()) {
      setAppUserLoading(true);
      terraKit
        .user()
        .getUserProfile()
        .then((appProfile) => {
          const userPhone = appProfile.value?.telephone;
          if (userPhone) {
            setAppUserPhone(userPhone);
          }
        })
        .finally(() => {
          setAppUserLoading(false);
        });
    } else {
      setAppUserLoading(false);
    }
  }, []);

  if (appUserLoading) return null;

  return (
    <BuilderComponent<ElementInterface, ExtraStoreState>
      componentDict={buildComponentDict({
        ...ecomComponents,
        ...coreComponents,
      })}
      content={content}
      ssrData={pbSSRData}
      configs={{
        device,
        env: getEnv('LANDING_BUILDER_ENV') as BuilderConfig['env'],
        globalData: {
          menu,
          userPhoneNumber:
            user?.telephone ||
            // in case webview in app consumer
            appUserPhone,
          isDisableLuckyDrawPhoneInput,
          websiteId,
          generateCustomUrl: (slug) =>
            `https://${window?.location?.hostname}/${slug}`,
        },
        terminalCode: terminal,
        terminalId: terminalId,
        locationCode: location,
        mappingDynamicSlots,
        featureFlags: { flagPB5049: true },
      }}
    />
  );
};
